import { useEffect } from 'react';
import { MapCanvas } from './map/MapCanvas.tsx';
import { TopBar } from './panels/TopBar.tsx';
import { Legend } from './panels/Legend.tsx';
import { DetailPanel } from './panels/DetailPanel.tsx';
import { BackupTimeline } from './panels/BackupTimeline.tsx';
import { AuthScreen } from './screens/AuthScreen.tsx';
import { useStore } from './state/store.ts';
import { computeLayout } from './map/layout.ts';
import { relativeTime } from './format.ts';

export function App() {
  const bootstrap = useStore((s) => s.bootstrap);
  const session = useStore((s) => s.session);
  const snapshot = useStore((s) => s.snapshot);
  const booting = useStore((s) => s.booting);
  const notice = useStore((s) => s.notice);
  const dismissNotice = useStore((s) => s.dismissNotice);
  const selectedId = useStore((s) => s.selectedId);
  const filters = useStore((s) => s.filters);
  const showLogin = useStore((s) => s.showLogin);
  const setShowLogin = useStore((s) => s.setShowLogin);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    if (session?.appName) document.title = session.appName;
  }, [session?.appName]);

  if (booting) {
    return <div className="auth" />;
  }

  if (session?.setupRequired && !showLogin) {
    return (
      <AuthScreen
        mode="setup"
        // With a directory configured, creating a local account is a choice,
        // not a toll gate: the first domain administrator can simply sign in.
        onSwitchToLogin={session.ldapEnabled ? () => setShowLogin(true) : undefined}
      />
    );
  }

  if (session?.setupRequired && showLogin) {
    return <AuthScreen mode="login" onCancel={() => setShowLogin(false)} />;
  }

  // Nothing to see without an account: reaching the port is not permission.
  if (!session?.authenticated) {
    return <AuthScreen mode="login" />;
  }

  // The panel resolves its node from the *current* snapshot so a poll landing
  // mid-inspection updates the numbers instead of dropping the selection.
  const layout = snapshot
    ? computeLayout(snapshot, {
        hideStopped: filters.hideStopped,
        hideTemplates: filters.hideTemplates,
        showBridges: filters.showBridges,
      })
    : null;
  const selected = selectedId && layout ? layout.byId.get(selectedId) ?? null : null;

  return (
    <div className="app">
      <TopBar snapshot={snapshot} />

      <div className="stage">
        {snapshot ? (
          <>
            <MapCanvas snapshot={snapshot} />
            <Legend />
            {selected && <DetailPanel node={selected} />}
            <StatusBanner />
          </>
        ) : (
          <div className="center-note">Waiting for the first poll…</div>
        )}
      </div>

      {snapshot && <BackupTimeline snapshot={snapshot} />}

      {notice && (
        <div className={`toast ${notice.kind}`} role="status">
          <span>{notice.text}</span>
          <button type="button" onClick={dismissNotice} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Honesty about data quality. A monitoring tool that shows stale numbers
 * without saying so is worse than one that shows nothing.
 */
function StatusBanner() {
  const snapshot = useStore((s) => s.snapshot);
  if (!snapshot) return null;

  if (snapshot.stale) {
    return (
      <div className="banner danger" role="alert">
        <strong>No contact with Proxmox.</strong>
        <span>
          Showing the last data received {relativeTime(snapshot.lastSuccessfulPollAt)}.
        </span>
      </div>
    );
  }

  if (snapshot.degraded) {
    const down = snapshot.endpoints.filter((e) => e.state === 'down');
    return (
      <div className="banner" role="status">
        <strong>Running on a reduced set of endpoints.</strong>
        <span>{down.map((e) => `${e.label} is down`).join(', ')}.</span>
      </div>
    );
  }

  if (snapshot.quorate === false) {
    return (
      <div className="banner" role="alert">
        <strong>Cluster is not quorate.</strong>
        <span>Guest state may be unreliable until quorum returns.</span>
      </div>
    );
  }

  return null;
}
